import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FeatureGate } from './FeatureGate';
import { FeatureFlagName } from '@curbside-quest/shared';

interface NavigationItem {
  path: string;
  label: string;
  icon: string;
  featureFlag?: FeatureFlagName;
  requiresAuth?: boolean;
  requiresAdmin?: boolean;
  guestAction?: 'AUTH_REQUIRED';
}

interface FeatureAwareNavigationProps {
  items: NavigationItem[];
  user?: any;
  isAdmin?: boolean;
  onItemClick?: () => void;
  onGuestAction?: (action: 'AUTH_REQUIRED') => void;
  className?: string;
  itemClassName?: (item: NavigationItem, isActive: boolean) => string;
  renderItem?: (item: NavigationItem, isActive: boolean, defaultContent: React.ReactNode) => React.ReactNode;
  mobileLayout?: boolean;
}

/**
 * Navigation component that respects feature flags and user permissions
 */
export function FeatureAwareNavigation({
  items,
  user,
  isAdmin = false,
  onItemClick,
  onGuestAction,
  className = '',
  itemClassName,
  renderItem,
  mobileLayout = false
}: FeatureAwareNavigationProps) {
  const location = useLocation();

  const filteredItems = items.filter(item => {
    // Check admin requirements
    if (item.requiresAdmin && !isAdmin) return false;
    // Show items with guestAction to all users (authenticated and guests)
    if (item.guestAction) return true;
    // Check authentication requirements for other items
    if (item.requiresAuth && !user) return false;
    return true;
  });

  return (
    <nav className={className}>
      {filteredItems.map((item) => {
        const isActive = location.pathname === item.path;
        
        const handleClick = (e: React.MouseEvent) => {
          // Handle guest actions
          if (item.guestAction && !user) {
            e.preventDefault();
            onGuestAction?.(item.guestAction);
            return;
          }
          // Normal navigation
          onItemClick?.();
        };

        const iconSpacing = mobileLayout ? '' : 'mr-3';
        const iconSize = mobileLayout ? 'text-xl' : '';
        const labelSize = mobileLayout ? 'text-xs font-medium leading-tight' : '';

        const defaultContent = item.guestAction && !user ? (
          <button
            className={itemClassName ? itemClassName(item, isActive) : ''}
            onClick={handleClick}
          >
            <span className={`${iconSpacing} ${iconSize}`} aria-hidden="true">{item.icon}</span>
            <span className={labelSize}>{item.label}</span>
          </button>
        ) : (
          <Link
            to={item.path}
            className={itemClassName ? itemClassName(item, isActive) : ''}
            onClick={handleClick}
          >
            <span className={`${iconSpacing} ${iconSize}`} aria-hidden="true">{item.icon}</span>
            <span className={labelSize}>{item.label}</span>
          </Link>
        );

        const content = renderItem ? renderItem(item, isActive, defaultContent) : defaultContent;

        // If the item has a feature flag, wrap it in FeatureGate
        if (item.featureFlag) {
          return (
            <FeatureGate key={item.path} flag={item.featureFlag}>
              {content}
            </FeatureGate>
          );
        }

        return <React.Fragment key={item.path}>{content}</React.Fragment>;
      })}
    </nav>
  );
}

/**
 * Default navigation items configuration
 */
export const defaultNavigationItems: NavigationItem[] = [
  {
    path: '/',
    label: 'Home',
    icon: '🏠'
  },
  {
    path: '/treasures',
    label: 'Treasures',
    icon: '🏆'
  },
  {
    path: '/submit',
    label: 'Submit Treasure',
    icon: '📤',
    guestAction: 'AUTH_REQUIRED'
  },
  {
    path: '/admin',
    label: 'Admin Dashboard',
    icon: '🛡️',
    requiresAuth: true,
    requiresAdmin: true
  },
  {
    path: '/advanced-search',
    label: 'Advanced Search',
    icon: '🔬',
    featureFlag: 'ADVANCED_SEARCH_ENABLED'
  },
  {
    path: '/premium',
    label: 'Premium Features',
    icon: '⭐',
    featureFlag: 'PREMIUM_FEATURES_ENABLED'
  }
];

/**
 * Mobile tab bar items (subset of navigation items)
 */
export const mobileTabItems: NavigationItem[] = [
  {
    path: '/',
    label: 'Home',
    icon: '🏠'
  },
  {
    path: '/treasures',
    label: 'Treasures',
    icon: '🏆'
  },
  {
    path: '/submit',
    label: 'Submit',
    icon: '📤',
    guestAction: 'AUTH_REQUIRED'
  },
  {
    path: '/advanced-search',
    label: 'Search',
    icon: '🔬',
    featureFlag: 'ADVANCED_SEARCH_ENABLED'
  }
];